export const STRATEGY_LABELS = {
  buy_hold: "Buy and hold",
  custom: "Custom Tech",
  equal_weight: "Equal weight",
  nasdaq100: "Nasdaq-100",
  nasdaq_composite: "Nasdaq Composite",
  volume: "Volume",
  volume_spike_damped_a70: "Spike-Damped Volume",
  volume_spike_damped_adjusted_a70_u60_v0_f25x30_e20:
    "Production Volume Strategy",
  volume_return_weighted_3_10: "Volume RW 3-10",
  volume_return_weighted_3_15: "Volume RW 3-15",
  volume_return_weighted_4_8: "Volume RW 4-8",
};

export const STRATEGY_COLORS = {
  buy_hold: "#2563eb",
  custom: "#e11d48",
  equal_weight: "#7c3aed",
  nasdaq100: "#dc2626",
  nasdaq_composite: "#0891b2",
  volume: "#2563eb",
  volume_spike_damped_a70: "#00a676",
  volume_spike_damped_adjusted_a70_u60_v0_f25x30_e20: "#00a676",
  volume_return_weighted_3_10: "#ca8a04",
  volume_return_weighted_3_15: "#ea580c",
  volume_return_weighted_4_8: "#16a34a",
};

export const STRATEGY_LINE_STYLES = {
  buy_hold: { dasharray: "2 6", strokeWidth: 3 },
  custom: { dasharray: "10 4", strokeWidth: 3.5 },
  equal_weight: { dasharray: "4 4", strokeWidth: 3 },
  nasdaq100: { dasharray: "1 5", strokeWidth: 3.5 },
  nasdaq_composite: { dasharray: "7 5", strokeWidth: 3.5 },
  volume: { dasharray: "9 4", strokeWidth: 3.5 },
  volume_spike_damped_a70: { dasharray: "", strokeWidth: 4 },
  volume_spike_damped_adjusted_a70_u60_v0_f25x30_e20: {
    dasharray: "",
    strokeWidth: 4,
  },
  volume_return_weighted_3_10: { dasharray: "9 3", strokeWidth: 3.75 },
  volume_return_weighted_3_15: { dasharray: "12 4 3 4", strokeWidth: 3.75 },
  volume_return_weighted_4_8: { dasharray: "5 3", strokeWidth: 3.75 },
};

export const STRATEGY_DESCRIPTIONS = {
  buy_hold:
    "Buys every stock that passes the replay window's initial eligibility filters at the first trading date, with target weights from that initial eligible universe, then holds those positions until final liquidation. Monthly market values change with prices, but the strategy does not add replacement names after the initial purchase.",
  custom:
    "Buys 50% GOOGL, 30% MSFT, and 20% AMZN at the start of each replay window, then holds them. Results use the same execution prices, transaction costs, realized-gain taxes, split handling, and terminal valuation rules as the simulated stock strategies.",
  equal_weight:
    "Rebalances monthly toward equal weights across the eligible universe. It uses the same trade accounting as the active strategies, including transaction costs, realized-gain taxes, split handling, and final liquidation.",
  nasdaq100:
    "Official Nasdaq-100 index benchmark scaled to the same initial portfolio value. It is not a stock-trade simulation: there are no holdings, transaction costs, or tax effects in this benchmark series.",
  nasdaq_composite:
    "Official Nasdaq Composite index benchmark scaled to the same initial portfolio value. It is not a stock-trade simulation: there are no holdings, transaction costs, or tax effects in this benchmark series.",
  volume:
    "Monthly active strategy that ranks eligible stocks by prior-date dollar volume, selects the highest-volume candidates, applies portfolio controls and trade limits, then rebalances using the configured execution-price assumption. Results include modeled costs, realized taxes, split handling, and terminal marked holdings unless an artifact explicitly models liquidation.",
  volume_spike_damped_a70:
    "Monthly active strategy that starts from lagged dollar-volume rank, discounts unstable volume spikes using slower volume and relative-volume features, then uses marginal tax-aware rebalancing with portfolio controls and trade limits. Results include modeled costs, realized taxes, split handling, and terminal marked holdings unless an artifact explicitly models liquidation.",
  volume_spike_damped_adjusted_a70_u60_v0_f25x30_e20:
    "Current production strategy. It ranks eligible stocks using lagged spike-damped dollar volume, upward price-path consistency, and point-in-time fundamental support, then applies marginal tax-aware rebalancing, linear winner retention, and a staged drawdown policy that reduces total stock exposure during confirmed broad market weakness.",
  volume_return_weighted_3_10:
    "Monthly active strategy that ranks candidates by prior-date dollar volume, skips non-positive trailing-return names, selects 20 positive-return names in volume order, then tilts weights by trailing annualized return with 3% minimum and 10% maximum position weights.",
  volume_return_weighted_3_15:
    "Monthly active strategy that ranks candidates by prior-date dollar volume, skips non-positive trailing-return names, selects 20 positive-return names in volume order, then tilts weights by trailing annualized return with 3% minimum and 15% maximum position weights.",
  volume_return_weighted_4_8:
    "Monthly active strategy that ranks candidates by prior-date dollar volume, skips non-positive trailing-return names, selects 20 positive-return names in volume order, then tilts weights by trailing annualized return with 4% minimum and 8% maximum position weights.",
};

const DEPRECATED_STRATEGIES = new Set([
  "buy_hold",
  "cap_weight",
  "capped_custom_equal_weight",
  "custom_buy_hold",
  "equal_weight",
]);

export const SIMULATION_NOTES = [
  "Each replay window starts with the configured initial cash and simulates trades through the window dates.",
  "Portfolio value includes cash plus marked holdings at the replay price for the selected date.",
  "Transaction costs include modeled commission, spread, slippage, market impact, and regulatory fees when present in the replay output.",
  "Taxes are accrued on realized gains and paid according to the simulator tax-payment schedule; after-tax return and value reflect those tax effects.",
  "Default ongoing replay values are cash plus marked holdings; any unpaid realized taxes are paid in the replay output. Some legacy diagnostic artifacts may include explicit terminal liquidation.",
  "Splits and reverse splits are reflected by the simulator before these replay artifacts are published.",
  "Nasdaq index benchmark series are scaled index values, so they have no simulated holdings, trades, taxes, or transaction costs.",
];

export function sortedRuns(index) {
  return [...(index?.runs || [])].sort((left, right) =>
    String(right.created_at_utc || "").localeCompare(left.created_at_utc || ""),
  );
}

export function sortedWindows(windowsPayload) {
  return [...(windowsPayload?.windows || [])].sort((left, right) => {
    const byYears = Number(right.window_years) - Number(left.window_years);
    if (byYears !== 0) {
      return byYears;
    }
    return String(right.window_start).localeCompare(String(left.window_start));
  });
}

export function windowSeries(seriesPayload, windowId) {
  return Object.fromEntries(
    Object.entries(seriesPayload?.windows?.[windowId] || {}).filter(
      ([strategy]) => isVisibleStrategy(strategy),
    ),
  );
}

export function maxReplaySteps(seriesByStrategy) {
  const lengths = Object.values(seriesByStrategy).map((points) => points.length);
  return Math.max(1, ...lengths);
}

export function horizonStrategyStats(windowsPayload) {
  const byHorizon = new Map();
  for (const windowRow of windowsPayload?.windows || []) {
    const horizon = Number(windowRow.window_years);
    if (!Number.isFinite(horizon)) {
      continue;
    }
    if (!byHorizon.has(horizon)) {
      byHorizon.set(horizon, new Map());
    }
    const strategies = byHorizon.get(horizon);
    const addedStrategies = new Set();
    for (const [strategy, metrics] of Object.entries(
      windowRow.strategies || {},
    )) {
      if (!isVisibleStrategy(strategy)) {
        continue;
      }
      addStrategyStat(strategies, strategy, metrics);
      addedStrategies.add(strategy);
    }
    for (const [benchmark, metrics] of Object.entries(
      windowRow.benchmarks || {},
    )) {
      const strategy = benchmark;
      if (!isVisibleStrategy(strategy) || addedStrategies.has(strategy)) {
        continue;
      }
      addStrategyStat(strategies, strategy, metrics);
    }
  }
  return [...byHorizon.entries()]
    .sort(([left], [right]) => left - right)
    .map(([horizon, strategies]) => ({
      horizon,
      strategies: [...strategies.entries()]
        .map(([strategy, values]) => aggregateStrategyStats(strategy, values))
        .sort((left, right) => left.label.localeCompare(right.label)),
    }));
}

function addStrategyStat(strategies, strategy, metrics) {
  const returnValue = numberOrNull(metrics?.after_tax_total_return);
  if (returnValue === null) {
    return;
  }
  if (!strategies.has(strategy)) {
    strategies.set(strategy, []);
  }
  strategies.get(strategy).push({
    intermediateTrades: numberOrNull(metrics?.intermediate_rebalance_trades),
    returnValue,
    trades: numberOrNull(metrics?.trades),
  });
}

function aggregateStrategyStats(strategy, values) {
  const returns = values
    .map((value) => value.returnValue)
    .filter((value) => value !== null);
  const trades = values
    .map((value) => value.trades)
    .filter((value) => value !== null);
  const intermediateTrades = values
    .map((value) => value.intermediateTrades)
    .filter((value) => value !== null);
  return {
    strategy,
    label: STRATEGY_LABELS[strategy] || strategy,
    meanReturn: mean(returns),
    minReturn: returns.length ? Math.min(...returns) : null,
    p05Return: percentile(returns, 0.05),
    maxReturn: returns.length ? Math.max(...returns) : null,
    meanIntermediateTrades: intermediateTrades.length
      ? mean(intermediateTrades)
      : null,
    meanTrades: trades.length ? mean(trades) : null,
    windowCount: returns.length,
  };
}

function mean(values) {
  if (!values.length) {
    return null;
  }
  return values.reduce((total, value) => total + value, 0) / values.length;
}

function percentile(values, p) {
  if (!values.length) {
    return null;
  }
  const sorted = [...values].sort((left, right) => left - right);
  const position = (sorted.length - 1) * p;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) {
    return sorted[lower];
  }
  return (
    sorted[lower] * (upper - position) +
    sorted[upper] * (position - lower)
  );
}

export function snapshotRows({
  costsByStrategy,
  currentDate,
  decisionsByStrategy,
  replayIndex,
  seriesByStrategy,
}) {
  return Object.entries(seriesByStrategy)
    .map(([strategy, points]) => {
      const pointIndex = Math.min(replayIndex, points.length - 1);
      const point = points[pointIndex] || {};
      const decisionSummary = exactDecisionSummary(
        decisionsByStrategy,
        strategy,
        currentDate || point.date,
      );
      const strategyCosts = costsByStrategy?.[strategy] || {};
      const rowDate = currentDate || point.date;
      const intermediateTrades =
        cumulativeIntermediateTradeCount(
          decisionsByStrategy,
          strategy,
          rowDate,
        ) ?? numberOrNull(strategyCosts.intermediate_rebalance_trades);
      return {
        strategy,
        label: STRATEGY_LABELS[strategy] || strategy,
        portfolioValue: numberOrNull(point.portfolio_value),
        returnValue: numberOrNull(point.after_tax_total_return),
        date: point.date,
        transactionCost: cumulativeTransactionCost(points, pointIndex),
        trades: cumulativeTradeCount(
          points,
          pointIndex,
          decisionSummary,
          decisionsByStrategy,
          strategy,
          strategyCosts,
          rowDate,
          intermediateTrades,
        ),
        intermediateTrades,
        taxes: cumulativeTaxPaid(points, pointIndex),
      };
    })
    .sort((left, right) => {
      return left.label.localeCompare(right.label);
    });
}

function exactDecisionSummary(decisionsByStrategy, strategy, date) {
  if (!date || !strategy) {
    return null;
  }
  const strategyDecisions = decisionsByStrategy?.[strategy];
  if (!strategyDecisions || Array.isArray(strategyDecisions)) {
    return null;
  }
  return (
    strategyDecisions[date]?.summary ||
    strategyDecisions[String(date)]?.summary ||
    null
  );
}

function stepTradeCount(point, decisionSummary) {
  const explicitTrades =
    numberOrNull(point.trades) ??
    numberOrNull(point.trade_count) ??
    numberOrNull(point.executed_trade_count);
  if (explicitTrades !== null) {
    return explicitTrades;
  }
  if (!decisionSummary) {
    return null;
  }
  return (
    (numberOrNull(decisionSummary.buy_count) || 0) +
    (numberOrNull(decisionSummary.sell_count) || 0)
  );
}

function stepTaxPaid(point) {
  return (
    numberOrNull(point.tax_paid) ??
    numberOrNull(point.estimated_tax) ??
    numberOrNull(point.tax_accrued)
  );
}

function cumulativeTransactionCost(points, index) {
  return cumulativeSum(points, index, (point) =>
    numberOrNull(point.transaction_cost),
  );
}

function cumulativeTaxPaid(points, index) {
  return cumulativeSum(points, index, stepTaxPaid);
}

function cumulativeTradeCount(
  points,
  index,
  decisionSummary,
  decisionsByStrategy,
  strategy,
  strategyCosts,
  date,
  minimumTrades,
) {
  const floor = numberOrNull(minimumTrades);
  const withFloor = (value) => {
    if (value === null) {
      return floor;
    }
    return floor === null ? value : Math.max(value, floor);
  };
  const explicit = cumulativeSum(points, index, (point) =>
    numberOrNull(point.trades) ??
    numberOrNull(point.trade_count) ??
    numberOrNull(point.executed_trade_count),
  );
  if (explicit !== null) {
    return withFloor(explicit);
  }
  if (index >= points.length - 1) {
    const totalTrades = numberOrNull(strategyCosts?.trades);
    if (totalTrades !== null) {
      return withFloor(totalTrades);
    }
  }
  const decisionTrades = cumulativeDecisionTradeCount(
    decisionsByStrategy,
    strategy,
    date,
  );
  if (decisionTrades !== null) {
    return withFloor(decisionTrades);
  }
  return withFloor(stepTradeCount(points[index] || {}, decisionSummary));
}

function cumulativeDecisionTradeCount(decisionsByStrategy, strategy, date) {
  const strategyDecisions = decisionsByStrategy?.[strategy];
  if (!strategyDecisions || Array.isArray(strategyDecisions)) {
    return null;
  }
  const dates = Object.keys(strategyDecisions)
    .filter((key) => isDateLikeKey(key))
    .sort();
  if (!dates.length) {
    return null;
  }
  let total = 0;
  let found = false;
  for (const decisionDate of dates) {
    if (date && decisionDate > date) {
      continue;
    }
    const summary = strategyDecisions[decisionDate]?.summary;
    if (!summary) {
      continue;
    }
    total +=
      (numberOrNull(summary.buy_count) || 0) +
      (numberOrNull(summary.sell_count) || 0);
    found = true;
  }
  return found ? total : null;
}

function cumulativeIntermediateTradeCount(decisionsByStrategy, strategy, date) {
  const strategyDecisions = decisionsByStrategy?.[strategy];
  if (!strategyDecisions || Array.isArray(strategyDecisions)) {
    return null;
  }
  const dates = Object.keys(strategyDecisions)
    .filter((key) => isDateLikeKey(key))
    .sort();
  if (dates.length <= 1) {
    return 0;
  }
  let total = 0;
  let found = false;
  for (const decisionDate of dates) {
    if (decisionDate === dates[0] || (date && decisionDate > date)) {
      continue;
    }
    const summary = strategyDecisions[decisionDate]?.summary;
    if (!summary) {
      continue;
    }
    total +=
      (numberOrNull(summary.buy_count) || 0) +
      (numberOrNull(summary.sell_count) || 0);
    found = true;
  }
  return found ? total : 0;
}

function cumulativeSum(points, index, valueFn) {
  let total = 0;
  let found = false;
  for (const point of points.slice(0, Math.max(0, index) + 1)) {
    const value = valueFn(point);
    if (value !== null) {
      total += value;
      found = true;
    }
  }
  return found ? total : null;
}

export function sortedStrategiesForWindow(holdingsPayload, windowId) {
  const windowHoldings = holdingsPayload?.windows?.[windowId];
  if (!windowHoldings || Array.isArray(windowHoldings)) {
    return [];
  }
  return Object.keys(windowHoldings)
    .filter((strategy) =>
      isVisibleStrategy(strategy) &&
      isStrategyHoldingsEntry(strategy, windowHoldings[strategy]),
    )
    .sort((left, right) =>
      (STRATEGY_LABELS[left] || left).localeCompare(
        STRATEGY_LABELS[right] || right,
      ),
    );
}

export function hasStrategyHoldingsForWindow(holdingsPayload, windowId, strategy) {
  const windowHoldings = holdingsPayload?.windows?.[windowId];
  return Boolean(
    strategy &&
      isVisibleStrategy(strategy) &&
      windowHoldings?.[strategy] &&
      isStrategyHoldingsEntry(strategy, windowHoldings[strategy]),
  );
}

export function hasStrategyDecisionsForWindow(decisionsPayload, windowId, strategy) {
  const windowDecisions = decisionsPayload?.windows?.[windowId];
  return Boolean(
    strategy &&
      isVisibleStrategy(strategy) &&
      windowDecisions?.[strategy] &&
      !Array.isArray(windowDecisions[strategy]),
  );
}

export function isVisibleStrategy(strategy) {
  return !DEPRECATED_STRATEGIES.has(String(strategy || ""));
}

export function holdingsForDate(
  holdingsPayload,
  windowId,
  date,
  strategy,
  limit = 5,
) {
  if (holdingsPayload?.dates) {
    return holdingsForDatePayload(holdingsPayload.dates, date, limit);
  }
  const windowHoldings = holdingsPayload?.windows?.[windowId];
  if (!windowHoldings) {
    return [];
  }
  if (
    strategy &&
    windowHoldings[strategy] &&
    !Array.isArray(windowHoldings[strategy])
  ) {
    return holdingsForDatePayload(windowHoldings[strategy], date, limit);
  }
  if (strategy) {
    return [];
  }
  if (!Array.isArray(windowHoldings) && !Array.isArray(windowHoldings[date])) {
    const firstStrategy = Object.keys(windowHoldings).find(
      (key) => windowHoldings[key] && !Array.isArray(windowHoldings[key]),
    );
    if (firstStrategy) {
      return holdingsForDatePayload(windowHoldings[firstStrategy], date, limit);
    }
  }
  return holdingsForDatePayload(windowHoldings, date, limit);
}

export function decisionsForDate(
  decisionsPayload,
  windowId,
  date,
  strategy,
  limit = 8,
) {
  if (decisionsPayload?.dates) {
    return decisionsForDatePayload(decisionsPayload.dates, date, limit);
  }
  const windowDecisions = decisionsPayload?.windows?.[windowId];
  if (!windowDecisions || !strategy || !windowDecisions[strategy]) {
    return emptyDecisionPayload();
  }
  return decisionsForDatePayload(windowDecisions[strategy], date, limit);
}

function holdingsForDatePayload(windowHoldings, date, limit) {
  if (Array.isArray(windowHoldings)) {
    return windowHoldings.slice(0, limit);
  }
  const exact = windowHoldings[date] || windowHoldings[String(date)];
  if (Array.isArray(exact)) {
    return exact.slice(0, limit);
  }
  const sortedDates = Object.keys(windowHoldings)
    .filter((key) => Array.isArray(windowHoldings[key]))
    .sort();
  const fallbackDate =
    sortedDates.filter((candidate) => !date || candidate <= date).at(-1) ||
    sortedDates[0];
  if (fallbackDate) {
    return windowHoldings[fallbackDate].slice(0, limit);
  }
  return [];
}

function decisionsForDatePayload(windowDecisions, date, limit) {
  const exact = windowDecisions?.[date] || windowDecisions?.[String(date)];
  if (exact) {
    return {
      rows: [...(exact.rows || [])].slice(0, limit),
      summary: exact.summary || emptyDecisionSummary(),
    };
  }
  const sortedDates = Object.keys(windowDecisions || {})
    .filter((key) => windowDecisions[key]?.rows)
    .sort();
  const fallbackDate =
    sortedDates.filter((candidate) => !date || candidate <= date).at(-1) ||
    sortedDates[0];
  if (!fallbackDate) {
    return emptyDecisionPayload();
  }
  const payload = windowDecisions[fallbackDate] || {};
  return {
    rows: [...(payload.rows || [])].slice(0, limit),
    summary: payload.summary || emptyDecisionSummary(),
  };
}

function emptyDecisionPayload() {
  return { rows: [], summary: emptyDecisionSummary() };
}

function emptyDecisionSummary() {
  return {
    buy_count: 0,
    candidate_count: 0,
    displayed_rows: 0,
    hold_count: 0,
    row_limit_applied: false,
    sell_count: 0,
    skip_count: 0,
    target_count: 0,
  };
}

function isStrategyHoldingsEntry(key, value) {
  if (isDateLikeKey(key) || !value) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value !== "object") {
    return false;
  }
  return Object.entries(value).some(
    ([dateKey, holdings]) =>
      isDateLikeKey(dateKey) &&
      Array.isArray(holdings) &&
      holdings.length > 0,
  );
}

function isDateLikeKey(key) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(key));
}

export function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "na";
  }
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 0,
    style: "currency",
  }).format(value);
}

export function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "na";
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "na";
  }
  return `${(value * 100).toFixed(1)}%`;
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
